import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, PackageCheck, RotateCcw } from "lucide-react";

const whatsappLink = (number, message) => {
  const phone = String(number || "").replace(/\D/g, "");
  return phone ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}` : "";
};

const pickupMessage = (row) => (
  `Halo ${row.customer_name || "Kak"}, kami dari LapakMahjong.id. `
  + `Mengonfirmasi jadwal pickup booking ${row.booking_code} hari ini pukul `
  + `${row.start_time || "—"}. `
  + "Sampai jumpa di Hub."
);

const returnMessage = (row) => (
  `Halo ${row.customer_name || "Kak"}, kami dari LapakMahjong.id. `
  + `Pengingat pengembalian untuk booking ${row.booking_code} paling lambat `
  + `${formatDue(row.return_due)}. Terima kasih.`
);

const formatDue = (value) => {
  if (!value) {
    return "hari ini";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.replace("T", " ").slice(0, 16);
  }
  return date.toLocaleString("id-ID", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ScheduleItem = ({ row, type }) => {
  const isReturn = type === "return";
  const message = isReturn ? returnMessage(row) : pickupMessage(row);
  const href = whatsappLink(row.customer_whatsapp, message);
  const timeLabel = isReturn ? formatDue(row.return_due) : `${row.start_time || "—"} WIB`;

  return (
    <article
      data-testid={`${type}-schedule-row-${row.booking_id}`}
      className={[
        "grid gap-3 border-b border-black/10 py-4 last:border-b-0",
        "sm:grid-cols-[1fr_auto]",
      ].join(" ")}
    >
      <Link
        to={row.link}
        data-testid={`${type}-schedule-detail-${row.booking_id}`}
        className="min-w-0 transition-colors hover:text-lm-red"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span
            data-testid={`${type}-schedule-time-${row.booking_id}`}
            className="font-display text-lg"
          >
            {timeLabel}
          </span>
          <span
            data-testid={`${type}-schedule-code-${row.booking_id}`}
            className="text-xs font-semibold text-lm-red"
          >
            {row.booking_code}
          </span>
        </div>
        <p
          data-testid={`${type}-schedule-customer-${row.booking_id}`}
          className="mt-1 truncate text-sm font-semibold"
        >
          {row.customer_name || "Customer"}
        </p>
        <p
          data-testid={`${type}-schedule-meta-${row.booking_id}`}
          className="mt-1 text-xs text-ink-soft"
        >
          {row.hub || "Hub"} · {row.package || "Paket"} · {row.quantity || 0} meja
        </p>
      </Link>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={`${type}-schedule-whatsapp-${row.booking_id}`}
          className={[
            "inline-flex h-10 items-center justify-center gap-2 rounded-md border",
            "border-emerald-700/30 bg-emerald-50 px-4 text-sm font-semibold",
            "text-emerald-900 transition-colors hover:bg-emerald-100",
          ].join(" ")}
        >
          <MessageCircle size={15} />
          {isReturn ? "Ingatkan kembali" : "WA pickup"}
        </a>
      )}
    </article>
  );
};

const SchedulePanel = ({ title, icon, type, rows }) => {
  const Icon = icon;
  const emptyText = type === "return"
    ? "Tidak ada unit yang dijadwalkan kembali hari ini."
    : "Tidak ada booking terkonfirmasi untuk pickup hari ini.";
  return (
    <section data-testid={`${type}-schedule-panel`} className="border-y border-black/10 py-5">
      <div className="flex items-center gap-2">
        <Icon size={17} className="text-lm-red" />
        <h2 data-testid={`${type}-schedule-title`} className="text-base font-semibold md:text-lg">
          {title}
        </h2>
        <span
          data-testid={`${type}-schedule-count`}
          className="ml-auto text-xs font-semibold text-ink-soft"
        >
          {rows.length}
        </span>
      </div>
      <div className="mt-3">
        {rows.length === 0 ? (
          <p data-testid={`${type}-schedule-empty`} className="py-3 text-sm text-ink-soft">
            {emptyText}
          </p>
        ) : rows.map((row) => (
          <ScheduleItem key={row.booking_id} row={row} type={type} />
        ))}
      </div>
    </section>
  );
};

export const DailySchedules = ({ bookings = [], returns = [] }) => (
  <section
    data-testid="admin-daily-schedules"
    className="grid gap-8 lg:grid-cols-2 lg:gap-12"
  >
    <SchedulePanel
      title="Jadwal Booking Hari Ini"
      icon={PackageCheck}
      type="pickup"
      rows={bookings}
    />
    <SchedulePanel
      title="Jadwal Pengembalian Unit Hari Ini"
      icon={RotateCcw}
      type="return"
      rows={returns}
    />
  </section>
);